import PropTypes from 'prop-types';
import { useState } from 'react';
// @mui
import { Box, Card, Typography, Stack, Chip, Modal, IconButton, Grid, Divider, LinearProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import api from '../../../services/api';
import Iconify from '../../../components/iconify';

// ----------------------------------------------------------------------

const StyledCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[12],
    [theme.breakpoints.up('md')]: {
      transform: 'translateY(-8px)',
      boxShadow: theme.shadows[20],
    },
  },
  '&:hover .pokemon-image-box': {
    transform: 'scale(1.02)',
  },
}));

const StyledImageBox = styled(Box)({
  paddingTop: '100%',
  position: 'relative',
  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
  overflow: 'hidden',
  transition: 'transform 0.3s ease',
});

const StyledProductImg = styled('img')({
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '85%',
  height: '85%',
  objectFit: 'contain',
  position: 'absolute',
  filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15))',
});

const StyledPokedexNumber = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 6,
  left: 6,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: 'white',
  padding: '2px 6px',
  borderRadius: '8px',
  fontSize: '0.65rem',
  fontWeight: 700,
  zIndex: 1,
  backdropFilter: 'blur(4px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  [theme.breakpoints.up('sm')]: {
    bottom: 8,
    left: 8,
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '0.75rem',
  },
}));

const StyledGeneration = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 6,
  right: 6,
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  color: '#333',
  padding: '2px 6px',
  borderRadius: '8px',
  fontSize: '0.6rem',
  fontWeight: 700,
  zIndex: 1,
  backdropFilter: 'blur(4px)',
  border: '1px solid rgba(0, 0, 0, 0.1)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  textTransform: 'uppercase',
  [theme.breakpoints.up('sm')]: {
    top: 8,
    right: 8,
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '0.7rem',
  },
}));

const typeColors = {
  bug: '#92bc2c',
  dark: '#595761',
  dragon: '#0c69c8',
  electric: '#f2d94e',
  fairy: '#e91e63',
  fighting: '#d3425f',
  fire: '#fba54c',
  flying: '#a1bbec',
  ghost: '#5f6dbc',
  grass: '#5fbd58',
  ground: '#da7c4d',
  ice: '#75d0c1',
  normal: '#a0a29f',
  poison: '#b763cf',
  psychic: '#fa8581',
  rock: '#c9bb8a',
  steel: '#5695a3',
  water: '#539ddf',
};

// ----------------------------------------------------------------------

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '700px',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  borderRadius: '20px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  overflow: 'hidden',
};

// ----------------------------------------------------------------------

export default function PokemonCard({ id, name, image, types, generation }) {
  const [open, setOpen] = useState(false);
  const [pokemonDetails, setPokemonDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showMoves, setShowMoves] = useState(false);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [selectedMove, setSelectedMove] = useState(null);
  const [loadingMove, setLoadingMove] = useState(false);

  const getTypeColor = (typeName) => typeColors[typeName] || '#999';
  
  const formatGeneration = (gen) => {
    // Converte "generation-i" para "Gen I", "generation-ii" para "Gen II", etc.
    if (!gen) return '';
    const genNumber = gen.split('-')[1].toUpperCase();
    return `Gen ${genNumber}`;
  };

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const response = await api.get(`pokemon/${id}/`);
      const speciesResponse = await api.get(`pokemon-species/${id}/`);
      
      setPokemonDetails({
        ...response.data,
        species: speciesResponse.data,
      });
    } catch (error) {
      console.error('Error fetching pokemon details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => setOpen(false);

  const handleMoveClick = async (moveName) => {
    setMoveModalOpen(true);
    setLoadingMove(true);
    try {
      const response = await api.get(`move/${moveName}/`);
      setSelectedMove(response.data);
    } catch (error) {
      console.error('Error fetching move details:', error);
    } finally {
      setLoadingMove(false);
    }
  };

  const handleMoveModalClose = () => {
    setMoveModalOpen(false);
    setSelectedMove(null);
  };

  return (
    <>
    <StyledCard onClick={handleOpen}>
      <StyledImageBox className="pokemon-image-box">
        <StyledProductImg alt={name} src={image} />
        <StyledPokedexNumber>
          #{String(id).padStart(3, '0')}
        </StyledPokedexNumber>
        <StyledGeneration>
          {formatGeneration(generation)}
        </StyledGeneration>
      </StyledImageBox>

      <Stack spacing={{ xs: 0.5, sm: 1, md: 1.5 }} sx={{ p: { xs: 1, sm: 1.5, md: 2.5 } }}>
        <Typography 
          textAlign="center" 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 700,
            textTransform: 'capitalize',
            fontSize: { xs: '0.75rem', sm: '0.9rem', md: '1.1rem' },
            lineHeight: 1.2,
          }}
        >
          {name}
        </Typography>

        <Stack 
          direction="row" 
          spacing={{ xs: 0.3, sm: 0.5 }}
          justifyContent="center"
          flexWrap="wrap"
          sx={{ gap: { xs: 0.3, sm: 0.5 } }}
        >
          {types.map((type, index) => (
            <Chip
              key={index}
              label={type.type.name}
              size="small"
              sx={{
                bgcolor: getTypeColor(type.type.name),
                color: 'white',
                fontWeight: 600,
                textTransform: 'capitalize',
                fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.75rem' },
                height: { xs: 18, sm: 20, md: 24 },
                '& .MuiChip-label': {
                  px: { xs: 0.75, sm: 1, md: 1.5 },
                },
              }}
            />
          ))}
        </Stack>
      </Stack>
    </StyledCard>

    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="pokemon-details-modal"
    >
      <Box sx={modalStyle}>
        {/* Header */}
        <Box
          sx={{
            position: 'relative',
            background: `linear-gradient(135deg, ${getTypeColor(types[0]?.type?.name)} 0%, ${getTypeColor(types[types.length - 1]?.type?.name)} 100%)`,
            p: 3,
            color: 'white',
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.2)',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.3)' },
            }}
          >
            <Iconify icon="eva:close-fill" />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box
              sx={{
                width: 120,
                height: 120,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(10px)',
              }}
            >
              <img
                src={image}
                alt={name}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 800, textTransform: 'capitalize', mb: 1 }}>
                {name}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                {types.map((type, index) => (
                  <Chip
                    key={index}
                    label={type.type.name}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      fontWeight: 600,
                      textTransform: 'capitalize',
                      backdropFilter: 'blur(10px)',
                    }}
                  />
                ))}
              </Stack>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                #{String(id).padStart(3, '0')} • {formatGeneration(generation)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, maxHeight: 'calc(90vh - 200px)', overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ py: 4 }}>
              <LinearProgress />
              <Typography textAlign="center" sx={{ mt: 2 }} color="text.secondary">
                Loading details...
              </Typography>
            </Box>
          ) : pokemonDetails ? (
            <Grid container spacing={3}>
              {/* Physical Stats */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Physical
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Height
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {(pokemonDetails.height / 10).toFixed(1)} m
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Weight
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {(pokemonDetails.weight / 10).toFixed(1)} kg
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              {/* Abilities */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Abilities
                </Typography>
                <Stack spacing={1}>
                  {pokemonDetails.abilities.map((ability, index) => (
                    <Chip
                      key={index}
                      label={ability.ability.name.replace('-', ' ')}
                      sx={{
                        textTransform: 'capitalize',
                        fontWeight: 600,
                        bgcolor: ability.is_hidden ? 'warning.lighter' : 'primary.lighter',
                        color: ability.is_hidden ? 'warning.darker' : 'primary.darker',
                        justifyContent: 'flex-start',
                      }}
                      icon={
                        ability.is_hidden ? (
                          <Iconify icon="eva:eye-off-fill" width={18} />
                        ) : (
                          <Iconify icon="eva:star-fill" width={18} />
                        )
                      }
                    />
                  ))}
                </Stack>
              </Grid>

              {/* Base Stats */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, mt: 2 }}>
                  Base Stats
                </Typography>
                <Stack spacing={2}>
                  {pokemonDetails.stats.map((stat, index) => (
                    <Box key={index}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                        >
                          {stat.stat.name.replace('-', ' ')}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          {stat.base_stat}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(stat.base_stat / 255) * 100}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            bgcolor: getTypeColor(types[0]?.type?.name),
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Grid>

              {/* Moves Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Moves
                  </Typography>
                  <IconButton
                    onClick={() => setShowMoves(!showMoves)}
                    sx={{
                      bgcolor: 'primary.lighter',
                      color: 'primary.main',
                      '&:hover': { bgcolor: 'primary.light' },
                    }}
                  >
                    <Iconify icon={showMoves ? 'eva:arrow-up-fill' : 'eva:arrow-down-fill'} />
                  </IconButton>
                </Box>
                
                {showMoves && (
                  <Box sx={{ maxHeight: 300, overflowY: 'auto', mt: 2 }}>
                    <Grid container spacing={1}>
                      {pokemonDetails.moves.map((moveData, index) => (
                        <Grid item xs={6} sm={4} md={3} key={index}>
                          <Chip
                            label={moveData.move.name.replace('-', ' ')}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveClick(moveData.move.name);
                            }}
                            sx={{
                              width: '100%',
                              textTransform: 'capitalize',
                              fontWeight: 500,
                              bgcolor: 'grey.200',
                              color: 'text.primary',
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: getTypeColor(types[0]?.type?.name),
                                color: 'white',
                              },
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      sx={{ display: 'block', mt: 2, textAlign: 'center' }}
                    >
                      Total: {pokemonDetails.moves.length} moves
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          ) : null}
        </Box>
      </Box>
    </Modal>

    {/* Move Details Modal */}
    <Modal
      open={moveModalOpen}
      onClose={handleMoveModalClose}
      aria-labelledby="move-details-modal"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '80vh',
        bgcolor: 'background.paper',
        borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <Box
          sx={{
            background: selectedMove?.type?.name 
              ? `linear-gradient(135deg, ${getTypeColor(selectedMove.type.name)} 0%, ${getTypeColor(selectedMove.type.name)}dd 100%)`
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            p: 3,
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
            {selectedMove?.name?.replace('-', ' ') || 'Loading...'}
          </Typography>
          <IconButton
            onClick={handleMoveModalClose}
            sx={{
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' },
            }}
          >
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, maxHeight: 'calc(80vh - 100px)', overflowY: 'auto' }}>
          {loadingMove ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography color="text.secondary">Loading move details...</Typography>
            </Box>
          ) : selectedMove ? (
            <Grid container spacing={3}>
              {/* Type and Category */}
              <Grid item xs={12}>
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                  <Chip
                    label={selectedMove.type?.name}
                    sx={{
                      bgcolor: getTypeColor(selectedMove.type?.name),
                      color: 'white',
                      fontWeight: 700,
                      textTransform: 'capitalize',
                      fontSize: '0.9rem',
                    }}
                  />
                  <Chip
                    label={selectedMove.damage_class?.name}
                    sx={{
                      bgcolor: 'grey.300',
                      fontWeight: 600,
                      textTransform: 'capitalize',
                      fontSize: '0.9rem',
                    }}
                  />
                </Stack>
              </Grid>

              {/* Description */}
              {selectedMove.effect_entries?.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                    {selectedMove.effect_entries[0]?.short_effect || selectedMove.effect_entries[0]?.effect}
                  </Typography>
                </Grid>
              )}

              {/* Stats Grid */}
              <Grid item xs={6}>
                <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Power
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>
                    {selectedMove.power || '-'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Accuracy
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'info.main' }}>
                    {selectedMove.accuracy ? `${selectedMove.accuracy}%` : '-'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    PP
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                    {selectedMove.pp || '-'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Priority
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {selectedMove.priority || 0}
                  </Typography>
                </Box>
              </Grid>

              {/* Additional Info */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, mt: 1 }}>
                  Additional Details
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Target
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                  {selectedMove.target?.name?.replace('-', ' ') || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Effect Chance
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedMove.effect_chance ? `${selectedMove.effect_chance}%` : 'N/A'}
                </Typography>
              </Grid>

              {selectedMove.meta && (
                <>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Crit Rate
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedMove.meta.crit_rate || 0}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Drain
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedMove.meta.drain || 0}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Healing
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedMove.meta.healing || 0}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Flinch Chance
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedMove.meta.flinch_chance || 0}%
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          ) : null}
        </Box>
      </Box>
    </Modal>
    </>
  );
}

PokemonCard.propTypes = {
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  types: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.shape({
        name: PropTypes.string.isRequired,
      }).isRequired,
    })
  ).isRequired,
  generation: PropTypes.string.isRequired,
};
